/**
 * Estendendo a classe File do Typescript para incluir novos métodos.
 */

export {};

declare global {
    interface File {

        /**
         * Gera uma representação Base64 do conteúdo do arquivo.
         *
         * Lê o arquivo usando `FileReader` e retorna apenas a parte de dados da
         * URL gerada (sem o prefixo `data:<mimetype>;base64,`), resultando em uma
         * string Base64 pura.
         *
         * O processo é assíncrono: a Promise é resolvida quando a leitura do
         * arquivo é concluída com sucesso, ou rejeitada caso ocorra algum erro
         * durante a leitura.
         *
         * @returns Promise que resolve com a string Base64 do arquivo.
         *
         * @throws {ProgressEvent} Caso ocorra um erro na leitura do arquivo pelo `FileReader`.
         *
         * @example
         * // Obtendo a string Base64 de um arquivo selecionado em um input
         * const file: File = inputElement.files[0];
         * const base64 = await file.toBase64();
         * console.log(base64); // "JVBERi0xLjQKJcfs..."
         *
         * @example
         * // Enviando o conteúdo do arquivo em uma requisição JSON
         * const payload = {
         *     filename: file.name,
         *     content: await file.toBase64(),
         * };
         */
        toBase64(): Promise<string>;

        /**
         * Gera um hash SHA-256 do arquivo usando a Web Crypto API (crypto.subtle).
         *
         * @returns Hash SHA-256 do arquivo em formato hexadecimal.
         *
         * @example
         * const hash = await file.toSHA256Hash();
         * console.log(hash); // "e3b0c44298fc1c149afb..."
         */
        toSHA256Hash(): Promise<string>;
    }
}

File.prototype.toBase64 = function (): Promise<string> {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => {
            const base64String = (reader.result as string).split(',')[1];
            resolve(base64String);
        };
        reader.onerror = (error) => reject(error);
        reader.readAsDataURL(this);
    });
};

File.prototype.toSHA256Hash = function (): Promise<string> {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = async () => {
            try {
                const arrayBuffer = reader.result as ArrayBuffer;
                const hashBuffer = await crypto.subtle.digest('SHA-256', arrayBuffer);
                const hashArray = Array.from(new Uint8Array(hashBuffer));
                const hashHex = hashArray
                    .map((byte) => byte.toString(16).padStart(2, '0'))
                    .join('');
                resolve(hashHex);
            } catch (error) {
                reject(error);
            }
        };
        reader.onerror = (error) => reject(error);
        reader.readAsArrayBuffer(this);
    });
};
